import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Check, Sparkles, AlertTriangle, Loader2, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { getMyBilling } from "@/lib/billing.functions";
import { supabase } from "@/integrations/supabase/client";
import { PaymentTestModeBanner } from "@/components/payment-test-mode-banner";
import { toast } from "sonner";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";

export const Route = createFileRoute("/_authenticated/app/upgrade")({ component: UpgradePage });

function UpgradePage() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<any>(null);
  const [user, setUser] = useState<{ id: string; email?: string } | null>(null);
  const loading = false;

  useEffect(() => {
    getMyBilling().then(setData).catch(() => {});
    supabase.auth.getUser().then(({ data: { user } }) => user && setUser({ id: user.id, email: user.email }));
  }, []);

  const PLANS = useMemo(() => {
    const en = i18n.resolvedLanguage?.startsWith("en");
    return [
      { id: "starter", name: "Starter", price: en ? "Free" : "Gratis", quota: 100, priceId: null as string | null,
        features: en
          ? ["100 AI queries / month", "1 user", "Email support"]
          : ["100 query AI / mese", "1 utente", "Supporto email"] },
      { id: "pro", name: "Pro", price: en ? "€49 / month" : "€49 / mese", quota: 1000, priceId: "pro_monthly", highlighted: true,
        features: en
          ? ["1,000 AI queries / month", "All modules", "Unlimited document analysis", "Priority support"]
          : ["1.000 query AI / mese", "Tutti i moduli", "Analisi documenti illimitata", "Supporto prioritario"] },
      { id: "studio", name: "Studio", price: en ? "€149 / seat" : "€149 / posto", quota: 10000, priceId: "studio_monthly",
        features: en
          ? ["10,000 queries / seat", "Up to 10 users", "Custom branding", "Account manager"]
          : ["10.000 query / posto", "Fino a 10 utenti", "Brand custom", "Account manager"] },
    ];
  }, [i18n.resolvedLanguage]);

  if (!data || !user) {
    return <div className="flex h-96 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-US" : "it-IT";
  const used = Number(data.used ?? 0);
  const limit = Number(data.limit ?? 0);
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const exhausted = limit > 0 && used >= limit;
  const nearLimit = !exhausted && pct >= 80;
  const currentPlan = String(data.plan ?? "starter");

  const buy = async (priceId: string | null) => {
    if (!priceId) return;
    try {
      openWhatsAppContact(`Ciao Gianluca, vorrei attivare il piano ${priceId} di LexAI Pro.`);
    } catch (e: any) {
      toast.error(e?.message ?? t("app.upgrade.checkoutError"));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PaymentTestModeBanner />
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-8">
        <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          <Sparkles className="h-3 w-3" /> {t("app.upgrade.eyebrow")}
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">
          {t("app.upgrade.title")}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          {t("app.upgrade.subtitle")}
        </p>

        <Card className={cn("mt-6 overflow-hidden border p-5",
          exhausted ? "border-destructive/40 bg-destructive/5"
          : nearLimit ? "border-amber-500/30 bg-amber-500/5"
          : "border-border/60")}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {t("app.upgrade.creditsCurrent", { plan: currentPlan })}
              </p>
              <p className="mt-1 font-display text-2xl font-semibold">
                {used.toLocaleString(locale)} <span className="text-muted-foreground">/ {limit.toLocaleString(locale)}</span>
                <span className="ml-2 align-middle text-xs text-muted-foreground">{t("app.upgrade.aiRequests")}</span>
              </p>
            </div>
            {exhausted && (
              <Badge variant="destructive" className="gap-1.5">
                <AlertTriangle className="h-3 w-3" /> {t("app.upgrade.exhausted")}
              </Badge>
            )}
          </div>
          <Progress value={pct} className="mt-4 h-2" />
          {exhausted && (
            <p className="mt-3 text-sm text-destructive">
              {t("app.upgrade.exhaustedHint")}
            </p>
          )}
        </Card>

        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {PLANS.map((p) => {
            const isCurrent = p.id === currentPlan;
            return (
              <Card key={p.id} className={cn("relative flex flex-col p-6",
                p.highlighted ? "border-primary/60 shadow-lg" : "border-border/60")}>
                {p.highlighted && (
                  <Badge className="absolute -top-2 right-4">{t("app.upgrade.recommended")}</Badge>
                )}
                <h3 className="font-display text-xl font-semibold">{p.name}</h3>
                <p className="mt-1 text-2xl font-semibold">{p.price}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("app.upgrade.perMonthQuota", { n: p.quota.toLocaleString(locale) })}
                </p>
                <ul className="mt-4 flex-1 space-y-2 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => buy(p.priceId)}
                  disabled={isCurrent || loading}
                  className="mt-6 w-full gap-2"
                  variant={p.highlighted ? "default" : "outline"}
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" />
                   : isCurrent ? t("app.upgrade.currentPlan")
                   : p.priceId ? <><Zap className="h-4 w-4" /> {t("app.upgrade.switchTo", { plan: p.name })}</>
                   : t("app.upgrade.included")}
                </Button>
              </Card>
            );
          })}
        </div>

        <div className="mt-8 flex justify-between text-xs text-muted-foreground">
          <Link to="/app/billing" className="underline-offset-4 hover:underline">{t("app.upgrade.manageCurrent")}</Link>
          <Link to="/app/dashboard" className="underline-offset-4 hover:underline">{t("app.upgrade.backToDashboard")}</Link>
        </div>
      </div>
    </div>
  );
}
